/** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 — Selected
 * Keyframe Detail Panel (spec §5/§7-§11/§33 section ③).
 *
 * Renders ONLY the currently-selected stage's own context — Duration →
 * Scoped Assets → Visual Description → Motion → Camera → Dialogue/
 * Audio, in that fixed order. NEVER renders the other N-1 hidden
 * keyframes' detail (spec §46 performance gate).
 *
 * KEYFRAME_SCOPED_ASSETS_CANONICAL_INPUT_ANCHORS_AND_PROVIDER_
 * CAPABILITY_DRIVEN_PARAMETERS_P0_V1 (P4/P5) — the bound keyframe IMAGE
 * is NEVER re-shown here. `KeyframeTimelineStrip`'s own thumbnail chip
 * is the ONE canonical image representation (select/open-source AND its
 * own × remove-image button already live there) — a second image row
 * in this panel was a literal duplicate of that same chip and has been
 * removed entirely (never re-add it as a "small" variant either). */

import { CustomerServiceOutlined, PlusOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  keyframeDurationToNext,
  resolveDialogueSegmentsForTime,
  type DialogueSegment,
  type InputAssetCategory,
  type InputAssetRef,
  type KeyframeRole,
  type KeyframeTimelineEntry,
} from '../videoKeyframeTimeline'
import VideoInputAssetsSection from './VideoInputAssetsSection'

const ROLE_LABEL_KEY: Record<KeyframeRole, string> = {
  start: 'generation.keyframeRoleStart',
  middle: 'generation.keyframeRoleMiddle',
  end: 'generation.keyframeRoleEnd',
}

export interface DialogueAudioSlotLike {
  ready: boolean
  sourceLabel: string
  nodeId: string
}

interface Props {
  workspaceId: string
  entry: KeyframeTimelineEntry | undefined
  /** Full timeline (spec §29/§30) — needed to compute this entry's
   * duration-to-NEXT-keyframe; a single `entry` alone cannot know its
   * neighbor. Never mutated here except via `onUpdateDurationToNext`
   * (spec §27 "选择/聚焦不能重写 Edge/顺序" — only an explicit duration edit
   * touches ordering, never a side effect of selection/focus). */
  timeline: KeyframeTimelineEntry[]
  dialogueSegments: DialogueSegment[]
  dialogueAudioAssignment: Map<string, DialogueAudioSlotLike>
  disabled?: boolean
  onUpdateEntry: (patch: Partial<KeyframeTimelineEntry>) => void
  /** P7/P8 — editable "duration to next keyframe"; the ONE mutation
   * path into keyframe timing, cascades onto later entries (see
   * `updateKeyframeDurationToNext`). Omitted/no-op on the terminal
   * (last) keyframe, which has no "next" to define a duration against. */
  onUpdateDurationToNext: (seconds: number) => void
  onCreateDialogueAudioNode: (segmentId: string) => void
  onAddDialogueSegment: () => void
  onSelectSourceNode: (nodeId: string) => void
  /** KEYFRAME_SCOPED_ASSETS_P0_V1 (P3/P10) — Characters/Products/Scenes/
   * Other reference assets already filtered to THIS keyframe only
   * (`inputAssetRefsForKeyframe`, caller-computed — Detail Panel never
   * re-derives scoping itself, spec §11 "必须真正绑定 keyframe_id"). */
  assetRefs: InputAssetRef[]
  onRemoveAssetRef: (refId: string) => void
  onPickAssetImage: (category: InputAssetCategory, replaceRefId?: string) => void
  onAddAssetLabel: (category: InputAssetCategory, label: string) => void
  onPreviewAssetRef: (ref: InputAssetRef) => void
  /** VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
   * §九/§十/§十一 — the ONE combined "参考资产" collapse toggle lives in
   * `GenerateVideoNode.tsx` (a single header covering Character/Product/
   * Scene/Other-Reference/Reference-Video regardless of whether they are
   * rendered HERE, keyframe-scoped, or in the legacy global tray) — this
   * panel only ever forwards the flag to its own `VideoInputAssetsSection`,
   * never owns a second/duplicate toggle. Duration/Description/Motion/
   * Camera/Dialogue below are NEVER affected (spec §九 "关键帧不要跟着参考资产
   * 一起折叠"). */
  referenceAssetsCollapsed?: boolean
  sectionHandles?: boolean
  /** VistoeraTASK-00064 P1/P4/P6 — forwarded verbatim to this panel's own
   * `VideoInputAssetsSection` (never a second drag contract). */
  mentionDraggable?: boolean
}

export default function KeyframeDetailPanel({
  workspaceId,
  entry,
  timeline,
  dialogueSegments,
  dialogueAudioAssignment,
  disabled,
  onUpdateEntry,
  onUpdateDurationToNext,
  onCreateDialogueAudioNode,
  onAddDialogueSegment,
  onSelectSourceNode,
  assetRefs,
  onRemoveAssetRef,
  onPickAssetImage,
  onAddAssetLabel,
  onPreviewAssetRef,
  referenceAssetsCollapsed,
  sectionHandles,
  mentionDraggable,
}: Props) {
  const { t } = useTranslation('canvas')

  if (!entry) {
    return (
      <div
        className="pcv-brief-section pcv-video-keyframe-detail-section"
        data-testid="video-keyframe-detail"
        role="tabpanel"
      >
        <span className="pcv-brief-section-label">{t('generation.keyframeDetailSection')}</span>
        <div className="pcv-gen-result-empty pcv-video-keyframe-detail-empty">
          <span>{t('generation.keyframeDetailNoneHint')}</span>
        </div>
      </div>
    )
  }

  const matchingSegments = resolveDialogueSegmentsForTime(dialogueSegments, entry.relativeTimeSec)
  const duration = keyframeDurationToNext(timeline, entry.id)

  return (
    <div
      className="pcv-brief-section pcv-video-keyframe-detail-section"
      data-testid="video-keyframe-detail"
      role="tabpanel"
      data-keyframe-role={entry.role}
      data-keyframe-entry-id={entry.id}
    >
      <span className="pcv-brief-section-label">
        {t('generation.keyframeDetailSection')}
        <span className="pcv-brief-section-optional">{entry.relativeTimeSec.toFixed(1)}s</span>
      </span>

      {/* Segment Duration (P7/P8) — editable, cascades onto later
       * keyframes via `updateKeyframeDurationToNext` (the ONE timing
       * mutation path, `relativeTimeSec` stays the single source of
       * truth). Terminal (last) keyframe has no "next" to define a
       * duration against — shows a terminal-state label, no input. */}
      <div className="pcv-video-keyframe-detail-row" data-testid="video-keyframe-detail-duration">
        <span className="pcv-gen-field-label">{t('generation.keyframeDetailDuration')}</span>
        {duration.hasNext && duration.durationToNextSec != null && duration.nextRole ? (
          <DurationToNextInput
            key={entry.id}
            value={duration.durationToNextSec}
            nextRoleLabel={t(ROLE_LABEL_KEY[duration.nextRole])}
            disabled={disabled}
            onCommit={onUpdateDurationToNext}
          />
        ) : (
          <span className="pcv-video-keyframe-detail-duration">{t('generation.keyframeDurationTerminal')}</span>
        )}
      </div>

      {/* Scoped Assets (P3/P10/P11) — Characters/Products/Scenes/Other
       * reference assets belonging to THIS keyframe only; switching the
       * selected keyframe swaps this list entirely (caller already
       * filtered `assetRefs` by `entry.id`, never re-derived here). */}
      <VideoInputAssetsSection
        workspaceId={workspaceId}
        refs={assetRefs}
        disabled={disabled}
        variant="inline"
        collapsed={referenceAssetsCollapsed}
        sectionHandles={sectionHandles && !referenceAssetsCollapsed}
        keyframeEntryId={entry.id}
        onRemove={onRemoveAssetRef}
        onPickImage={onPickAssetImage}
        onAddLabel={onAddAssetLabel}
        onPreview={onPreviewAssetRef}
        mentionDraggable={mentionDraggable}
      />

      {/* Visual Description — stage-specific, distinct from Overall Prompt (spec §29) */}
      <div className="pcv-video-keyframe-detail-row">
        <span className="pcv-gen-field-label">{t('generation.keyframeDetailDescription')}</span>
        <textarea
          className="pcv-brief-textarea nodrag"
          rows={2}
          value={entry.visualDescription || ''}
          placeholder={t('generation.keyframeDescriptionPlaceholder')}
          disabled={disabled}
          onChange={(e) => onUpdateEntry({ visualDescription: e.target.value })}
        />
      </div>

      {/* Motion */}
      <div className="pcv-video-keyframe-detail-row">
        <span className="pcv-gen-field-label">{t('generation.keyframeDetailMotion')}</span>
        <input
          type="text"
          className="pcv-gen-text-input nodrag"
          value={entry.motionIntent || ''}
          placeholder={t('generation.keyframeMotionPlaceholder')}
          disabled={disabled}
          onChange={(e) => onUpdateEntry({ motionIntent: e.target.value })}
        />
      </div>

      {/* Camera */}
      <div className="pcv-video-keyframe-detail-row">
        <span className="pcv-gen-field-label">{t('generation.keyframeDetailCamera')}</span>
        <input
          type="text"
          className="pcv-gen-text-input nodrag"
          value={entry.cameraIntent || ''}
          placeholder={t('generation.keyframeCameraPlaceholder')}
          disabled={disabled}
          onChange={(e) => onUpdateEntry({ cameraIntent: e.target.value })}
        />
      </div>

      {/* Dialogue / Audio — Timeline Segment resolution (spec §10/§11/§38) */}
      <div className="pcv-video-keyframe-detail-row" data-testid="video-keyframe-detail-audio">
        <span className="pcv-gen-field-label">{t('generation.keyframeDetailAudio')}</span>
        {matchingSegments.length > 0 ? (
          <div className="pcv-video-dialogue-segment-list">
            {matchingSegments.map((seg) => {
              const audioSlot = dialogueAudioAssignment.get(seg.id)
              return (
                <div key={seg.id} className="pcv-video-dialogue-segment-row" data-testid="video-dialogue-segment">
                  <span className="pcv-video-dialogue-segment-speaker">{seg.speakerLabel || t('generation.keyframeDialogueUnknownSpeaker')}</span>
                  <span className="pcv-video-dialogue-segment-text" title={seg.text}>
                    {seg.text}
                  </span>
                  <span className="pcv-video-dialogue-segment-time">
                    {seg.startTimeSec.toFixed(1)}s–{seg.endTimeSec.toFixed(1)}s
                    {seg.provisionalTiming ? ` · ${t('generation.keyframeDialogueProvisional')}` : ''}
                  </span>
                  {audioSlot?.ready ? (
                    <span
                      className="pcv-video-dialogue-segment-audio-ready nodrag"
                      onClick={() => onSelectSourceNode(audioSlot.nodeId)}
                    >
                      <CustomerServiceOutlined /> {audioSlot.sourceLabel}
                    </span>
                  ) : seg.audioSourceNodeId ? (
                    <span className="pcv-video-dialogue-segment-audio-pending">{t('generation.keyframeDialoguePending')}</span>
                  ) : (
                    <button
                      type="button"
                      className="pcv-video-keyframe-detail-empty-action pcv-video-keyframe-detail-empty-action--sm nodrag"
                      data-testid="video-dialogue-generate-audio-btn"
                      disabled={disabled}
                      onClick={() => onCreateDialogueAudioNode(seg.id)}
                    >
                      <CustomerServiceOutlined /> {t('generation.keyframeGenerateAudio')}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="pcv-video-dialogue-segment-empty">
            <span>{t('generation.keyframeDialogueNone')}</span>
            <button type="button" className="pcv-video-keyframe-detail-empty-action pcv-video-keyframe-detail-empty-action--sm nodrag" disabled={disabled} onClick={onAddDialogueSegment}>
              <PlusOutlined /> {t('generation.keyframeDialogueAdd')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** P7/P8 — local edit buffer (spec §7 "改成可输入") so the field is a
 * normal free-typed number input; the cascade recalculation only fires
 * on commit (blur / Enter), never on every keystroke, so a mid-typing
 * partial value (e.g. "1.") never trips `updateKeyframeDurationToNext`.
 * Remounts (`key={entry.id}`) whenever the selected keyframe changes so
 * stale local state never leaks across stages. */
function DurationToNextInput({
  value,
  nextRoleLabel,
  disabled,
  onCommit,
}: {
  value: number
  nextRoleLabel: string
  disabled?: boolean
  onCommit: (seconds: number) => void
}) {
  const { t } = useTranslation('canvas')
  const [draft, setDraft] = useState(value.toFixed(1))

  const commit = () => {
    const parsed = Number.parseFloat(draft)
    if (Number.isFinite(parsed) && parsed >= 0 && Math.abs(parsed - value) > 0.001) {
      onCommit(parsed)
    } else {
      setDraft(value.toFixed(1))
    }
  }

  return (
    <span className="pcv-video-keyframe-detail-duration-edit nodrag">
      <input
        type="number"
        min={0}
        step={0.1}
        className="pcv-gen-text-input pcv-video-keyframe-duration-input"
        data-testid="video-keyframe-duration-input"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
      />
      <span className="pcv-video-keyframe-detail-duration-unit">
        s {t('generation.keyframeDurationToNextSuffix', { nextRole: nextRoleLabel })}
      </span>
    </span>
  )
}
